import { SEC_ApiCall, SEC_DataApiPost } from '../func';

/**
 * Validate staff-management API responses before the UI consumes them.
 * @param {any} response - Raw API payload returned by API Gateway.
 * @returns {{items: Array}} Staff list response.
 */
function ensureStaffListResponse(response) {
  if (!response || typeof response !== 'object') {
    throw new Error('Empty staff list response');
  }

  if (typeof response.message === 'string' && response.message) {
    throw new Error(response.message);
  }

  if (!Array.isArray(response.items)) {
    throw new Error('Unexpected staff list response shape');
  }

  return response;
}

/** Validate a staff-sync summary or detail response. */
function ensureStaffSyncResponse(response) {
  if (!response || typeof response !== 'object' || typeof response.enabled !== 'boolean') {
    throw new Error(response?.message || 'Unexpected staff sync response');
  }
  if (response.items !== undefined && !Array.isArray(response.items)) {
    throw new Error('Unexpected staff sync items');
  }
  return response;
}

/** Load staff-sync availability or the five newest staff runs. */
export async function getStaffSyncResults(summaryOnly = false) {
  const response = await SEC_ApiCall('sysmanager/learners/sync-results', {
    summaryOnly: summaryOnly ? 'true' : 'false',
    syncType: 'STAFF',
  });
  return ensureStaffSyncResponse(response);
}

/** Load safe error details for one staff-sync run. */
export async function getStaffSyncErrorDetails(logId) {
  const response = await SEC_ApiCall('sysmanager/learners/sync-results', {
    logId,
    syncType: 'STAFF',
  });
  return ensureStaffSyncResponse(response);
}

/**
 * Load active or disabled staff accounts for the System Manager staff page.
 * @param {'active'|'disabled'} status - Which staff account state to load.
 * @returns {Promise<{items: Array}>} Normalised staff list payload.
 */
export async function getSysManagerStaff(status = 'active') {
  const endpoint = status === 'disabled'
    ? 'sysmanager/staff/disabled'
    : 'sysmanager/staff/active';

  const response = await SEC_ApiCall(endpoint);
  return ensureStaffListResponse(response);
}

/** Create one invited staff account with its user type and permission group. */
export async function createSysManagerStaff(payload) {
  const response = await SEC_DataApiPost(
    'sysmanager/staff/create',
    JSON.stringify(payload),
    {},
  );
  if (!response || typeof response !== 'object' || response.status !== 'success' || !response.staff) {
    throw new Error(response?.message || 'Unexpected create staff response');
  }
  return response;
}

/**
 * Disable one active staff account.
 * @param {{targetCogId: string}} payload - Staff account identifier to disable.
 * @returns {Promise<object>} Disable response payload.
 */
export async function disableSysManagerStaff(payload) {
  const response = await SEC_DataApiPost(
    'sysmanager/staff/active/disable',
    JSON.stringify(payload),
    {},
  );

  if (!response || typeof response !== 'object') {
    throw new Error('Empty disable staff response');
  }
  if (typeof response.message === 'string' && response.message) {
    throw new Error(response.message);
  }
  if (response.status !== 'success') {
    throw new Error('Unexpected disable staff response shape');
  }

  return response;
}

/**
 * Enable one disabled staff account.
 * @param {{targetCogId: string}} payload - Staff account identifier to enable.
 * @returns {Promise<object>} Enable response payload.
 */
export async function enableSysManagerStaff(payload) {
  const response = await SEC_DataApiPost(
    'sysmanager/staff/disabled/enable',
    JSON.stringify(payload),
    {},
  );

  if (!response || typeof response !== 'object') {
    throw new Error('Empty enable staff response');
  }
  if (typeof response.message === 'string' && response.message) {
    throw new Error(response.message);
  }
  if (response.status !== 'success') {
    throw new Error('Unexpected enable staff response shape');
  }

  return response;
}

/**
 * Trigger Cognito's built-in password reset email for one active staff account.
 * @param {{targetCogId: string}} payload - Staff account identifier to reset.
 * @returns {Promise<object>} Reset response payload.
 */
export async function resetSysManagerStaffPassword(payload) {
  const response = await SEC_DataApiPost(
    'sysmanager/staff/active/resetPassword',
    JSON.stringify(payload),
    {},
  );

  if (!response || typeof response !== 'object') {
    throw new Error('Empty password reset response');
  }
  if (typeof response.message === 'string' && response.message) {
    throw new Error(response.message);
  }
  if (response.status !== 'success') {
    throw new Error('Unexpected password reset response shape');
  }

  return response;
}

/** Load the direct System Manager section permissions for one staff user. */
export async function getSysManagerStaffSystemAccess(targetCogId) {
  const response = await SEC_ApiCall('sysmanager/staff/system-access', { targetCogId });
  if (!response || typeof response !== 'object' || !response.systemAccess) {
    throw new Error(response?.message || 'Unexpected System Manager access response');
  }
  return response;
}

/** Update one direct System Manager section permission for a staff user. */
export async function updateSysManagerStaffSystemAccess(payload) {
  const response = await SEC_DataApiPost('sysmanager/staff/system-access/update', JSON.stringify(payload), {});
  if (!response || typeof response !== 'object' || !response.systemAccess) {
    throw new Error(response?.message || 'Unexpected System Manager access update response');
  }
  return response;
}

/** Load inherited, overridden and effective module permissions for one staff user. */
export async function getSysManagerStaffPermissionOverrides(targetCogId) {
  const response = await SEC_ApiCall('sysmanager/staff/permission-overrides', { targetCogId });
  if (!response || typeof response !== 'object' || !response.effectivePermissions) {
    throw new Error(response?.message || 'Unexpected permission overrides response');
  }
  return response;
}

/** Update one allow-listed individual permission override. */
export async function updateSysManagerStaffPermissionOverride(payload) {
  const response = await SEC_DataApiPost('sysmanager/staff/permission-overrides/update', JSON.stringify(payload), {});
  if (!response || typeof response !== 'object' || !response.effectivePermissions) {
    throw new Error(response?.message || 'Unexpected permission override update response');
  }
  return response;
}
